import React from 'react';  
import styled, { keyframes, css } from 'styled-components';  
import greenhouseImage from '../pages/images/greenhouse.png';  
  
// Keyframes for animations  
const rainAnimation = keyframes`  
  0% { opacity: 0; transform: translateY(-50px); }  
  30% { opacity: 1; }  
  100% { opacity: 0; transform: translateY(300px); }  
`;  
  
const lightChangeAnimation = keyframes`  
  0% { filter: brightness(100%); }  
  50% { filter: brightness(200%); }  
  100% { filter: brightness(100%); }  
`;  
  
const thunderAnimation = keyframes`  
  0%, 19%, 39%, 59%, 79%, 100% { opacity: 0; }  
  20%, 21%, 22%, 23%, 24%, 25%, 26%, 27%, 28%, 29% { opacity: 1; }  
  40%, 41%, 42%, 43%, 44%, 45%, 46%, 47%, 48%, 49% { opacity: 1; }  
  // 50%, 51%, 52%, 53%, 54%, 55%, 56%, 57%, 58%, 59% { opacity: 1; }  
  // 60%, 61%, 62%, 63%, 64%, 65%, 66%, 67%, 68%, 69% { opacity: 1; }  
  // 80%, 81%, 82%, 83%, 84%, 85%, 86%, 87%, 88%, 89% { opacity: 1; }  
`;  
  
const snowAnimation = keyframes`  
  0% { opacity: 0; transform: translateY(-20px); }  
  30% { opacity: 1; }  
  100% { opacity: 0; transform: translateY(200px); }  
`;  
  
const fogAnimation = keyframes`  
  0% { opacity: 0.6; }  
  50% { opacity: 0.8; }  
  100% { opacity: 0.6; }  
`;  
  
const windyAnimation = keyframes`  
  0% { transform: translateX(-100%); }  
  100% { transform: translateX(100%); }  
`;  
  
const iceAnimation = keyframes`  
  0% { opacity: 0; transform: translateY(-50px); }  
  30% { opacity: 1; }  
  100% { opacity: 0; transform: translateY(300px); }  
`;  
  
// Styled components  
const ImageContainer = styled.div`  
  position: relative;  
  aspect-ratio: 16 / 9;  
  background-color: rgba(0, 0, 0, 0.1);  
  border-radius: 10px;  
  overflow: hidden;  
  height: auto;  
`;  
  
const RainDrop = styled.div.attrs(props => ({  
  style: {  
    left: `${props.$left}%`,  
    animationDelay: `${props.$delay}s`,  
  },  
}))`  
  position: absolute;  
  top: 0;  
  width: 2px;  
  height: 15px;  
  background: rgba(0, 191, 255, 0.7);  
  border-radius: 50%;  
  animation: ${rainAnimation} 1s infinite ease-in;  
`;  
  
const SnowFlake = styled.div.attrs(props => ({  
  style: {  
    left: `${props.$left}%`,  
    animationDelay: `${props.$delay}s`,  
  },  
}))`  
  position: absolute;  
  top: 0;  
  width: 5px;  
  height: 5px;  
  background: white;  
  border-radius: 50%;  
  animation: ${snowAnimation} 2s infinite ease-in;  
`;  
  
const IceDrop = styled.div.attrs(props => ({  
  style: {  
    left: `${props.$left}%`,  
    animationDelay: `${props.$delay}s`,  
  },  
}))`  
  position: absolute;  
  top: 0;  
  width: 3px;  
  height: 15px;  
  background: rgba(173, 216, 230, 0.7);  
  border-radius: 50%;  
  animation: ${iceAnimation} 1.5s infinite ease-in;  
`;  
  
// Filter out isLightChange before passing props to the DOM element  
const AnimatedImage = styled(({ isLightChange, alt, ...props }) => <img alt={alt} {...props} />)`  
  position: absolute;  
  top: 0;  
  left: 0;  
  width: 100%;  
  height: 100%;  
  object-fit: contain;  
  border-radius: 20px;  
  ${({ isLightChange }) => isLightChange && css`  
    animation: ${lightChangeAnimation} 2s infinite;  
  `}  
`;  
  
const sunnyAnimation = keyframes`  
  0% { opacity: 0.8; transform: translate(-50%, -50%) scale(1); }  
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }  
  100% { opacity: 0.8; transform: translate(-50%, -50%) scale(1); }  
`;  
  
const SunnyEffect = styled.div`  
  position: absolute;  
  top: 20%;  
  left: 20%;  
  width: 100%;  
  height: 100%;  
  background: radial-gradient(circle, rgba(255, 223, 100, 0.6) 0%, rgba(0, 0, 0, 0.01) 70%);  
  transform: translate(-50%, -50%);  
  border-radius: 50%;  
  animation: ${sunnyAnimation} 2s linear infinite;  
`;  
  
const cloudyAnimation = keyframes`  
  0% { background-position: 0% 200%; }  
  100% { background-position: 0% 0%; }  
`;  
  
const CloudyEffect = styled.div`  
  position: absolute;  
  top: 0;  
  left: 0;  
  width: 100%;  
  height: 100%;  
  background: linear-gradient(to bottom, rgba(169,169,169,0.8) 0%, rgba(255,255,255,0.5) 40%, rgba(169,169,169,0.8) 10%);  
  background-size: 100% 200%;  
  transform: translateY(-80%);  
  opacity: 0.8;  
  animation: ${cloudyAnimation} 3s linear infinite;  
  filter: blur(5px);  
`;  
  
const ThunderEffect = styled.div`  
  position: absolute;  
  top: 0;  
  left: 0;  
  width: 100%;  
  height: 100%;  
  background: rgba(255, 255, 0, 0.5);  
  opacity: 0;  
  animation: ${thunderAnimation} 5s infinite;  
`;  

const FogEffect = styled.div`  
  position: absolute;  
  top: 0;  
  left: 0;  
  width: 100%;  
  height: 100%;  
  background: rgba(169, 169, 169, 0.5);  
  animation: ${fogAnimation} 3s infinite;  
`;  
  
const WindEffect = styled.div`  
  position: absolute;  
  top: 0;  
  left: 0;  
  width: 100%;  
  height: 100%;  
  background: rgba(240, 255, 255, 0.5);  
  animation: ${windyAnimation} 2s linear infinite;  
`;  
  
// Utility function to generate rain drops  
const generateRainDrops = () => {  
  const drops = [];  
  for (let i = 0; i < 100; i++) {  
    drops.push(  
      <RainDrop  
        key={i}  
        $left={Math.random() * 100}  
        $delay={Math.random() * 1}  
      />  
    );  
  }  
  return drops;  
};  
  
// Utility function to generate snow flakes  
const generateSnowFlakes = () => {  
  const flakes = [];  
  for (let i = 0; i < 100; i++) {  
    flakes.push(  
      <SnowFlake  
        key={i}  
        $left={Math.random() * 100}  
        $delay={Math.random() * 2}  
      />  
    );  
  }  
  return flakes;  
};  
  
// Utility function to generate ice drops  
const generateIceDrops = () => {  
  const drops = [];  
  for (let i = 0; i < 100; i++) {  
    drops.push(  
      <IceDrop  
        key={i}  
        $left={Math.random() * 100}  
        $delay={Math.random() * 1.5}  
      />  
    );  
  }  
  return drops;  
};  
  
// Main component  
const GreenhouseImage = ({ weather = 'sunny', isLightChange = false }) => (  
  <ImageContainer>  
    {weather === 'sunny' && <SunnyEffect />}  
    {weather === 'cloudy' && <CloudyEffect />}  
    {weather === 'rain' && generateRainDrops()}  
    {weather === 'thunderstorm' && <ThunderEffect />}  
    {weather === 'snow' && generateSnowFlakes()}  
    {weather === 'fog' && <FogEffect />}  
    {weather === 'windy' && <WindEffect />}  
    {weather === 'ice' && generateIceDrops()}  
    <AnimatedImage src={greenhouseImage} alt="Greenhouse" isLightChange={isLightChange} />  
  </ImageContainer>  
);  
  
export default GreenhouseImage;  
